import React from "react";
import { Box, Paper, TextField, Typography } from "@mui/material";
import WrappedButton from "./WrappedButton";
import { useHistory } from "react-router-dom";

type LoginStep = "login" | "verifyDob" | "changePassword";

interface LoginProps {
	setAuthenticated: () => void;
}

async function parseJsonResponse(response: Response): Promise<any> {
	const text = await response.text();
	if (!text) return null;
	try {
		return JSON.parse(text);
	} catch {
		return null;
	}
}

function formatDobInput(raw: string): string {
	const d = raw.replace(/\D/g, "").slice(0, 8);
	if (d.length <= 2) return d;
	if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
	return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

export default function Login(props: LoginProps) {
	const [user, setUser] = React.useState("");
	const [pass, setPass] = React.useState("");
	const [dateOfBirth, setDateOfBirth] = React.useState("");
	const [newPassword, setNewPassword] = React.useState("");
	const [confirmPassword, setConfirmPassword] = React.useState("");
	const [step, setStep] = React.useState<LoginStep>("login");
	const [pendingId, setPendingId] = React.useState<string | null>(null);
	const [isSaving, setSaving] = React.useState(false);
	const [errorMessage, setErrorMessage] = React.useState("");
	const [agencyName, setAgencyName] = React.useState("");
	const history = useHistory();

	const checkKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") submit();
	};

	React.useEffect(() => {
		fetch("login/IsLoggedIn")
			.then((dt) => parseJsonResponse(dt))
			.then((dt) => {
				if (dt === true) props.setAuthenticated();
			})
			.catch(() => {});

		fetch("login/AgencyName")
			.then((dt) => parseJsonResponse(dt))
			.then((dt) => setAgencyName(dt && dt.name ? dt.name : ""))
			.catch(() => {});

		// eslint-disable-next-line
	}, []);

	const submit = async () => {
		setSaving(true);
		setErrorMessage("");

		const body: Record<string, unknown> = {
			tzo: -(new Date("1/1/2009").getTimezoneOffset() / 60),
		};

		if (step === "login") {
			body.username = user;
			body.password = pass;
		} else if (pendingId) {
			body.pendingId = pendingId;
			if (step === "verifyDob") body.dateOfBirth = dateOfBirth;
			if (step === "changePassword") {
				body.newPassword = newPassword;
				body.confirmPassword = confirmPassword;
			}
		}

		try {
			const dt = await fetch("login/Authenticate", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				cache: "no-cache",
				body: JSON.stringify(body),
			});

			const res = await parseJsonResponse(dt);
			if (!dt.ok || !res) {
				setErrorMessage("Invalid email address or password.");
				setSaving(false);
				return;
			}

			if (res.success) {
				props.setAuthenticated();
				history.push("/MY_TASKS");
			} else if (res.step === "verifyDob" && res.pendingId) {
				setStep("verifyDob");
				setPendingId(res.pendingId);
				setErrorMessage("");
			} else if (res.step === "changePassword" && res.pendingId) {
				setStep("changePassword");
				setPendingId(res.pendingId);
				setErrorMessage("");
			} else {
				setErrorMessage(res.message || "Invalid email address or password.");
			}
		} catch {
			setErrorMessage("Login failed. Please try again.");
		}

		setSaving(false);
	};

	const buttonCaption = step === "verifyDob" ? "Verify" : step === "changePassword" ? "Change Password" : "Login";

	return (
		<Box
			sx={{
				height: 1,
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				flexDirection: "column",
				backgroundImage: "url('auth-background.jpg')",
				backgroundRepeat: "no-repeat",
				backgroundSize: "cover",
				backgroundColor: "background.default",
			}}>
			<Paper sx={{ p: 2, flexDirection: "column", display: "flex" }}>
				<Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
					<img
						src="DisplayImageLogo.aspx"
						alt={agencyName || ""}
						style={{ maxWidth: "250px", maxHeight: "120px", objectFit: "contain" }}
					/>
				</Box>
				{agencyName && (
					<Typography variant="h6" sx={{ mb: 2 }}>
						{agencyName}
					</Typography>
				)}

				{step === "login" && (
					<>
						<TextField
							label="Email Address"
							variant="outlined"
							value={user}
							onKeyUp={checkKeyUp}
							sx={{ marginBottom: 2 }}
							onChange={(e) => setUser(e.target.value)}
						/>
						<TextField
							label="Password"
							type="password"
							variant="outlined"
							value={pass}
							onKeyUp={checkKeyUp}
							sx={{ marginBottom: 2 }}
							onChange={(e) => setPass(e.target.value)}
						/>
					</>
				)}

				{step === "verifyDob" && (
					<>
						<Typography variant="body1" sx={{ mb: 1 }}>
							Please enter your date of birth below
						</Typography>
						<Typography variant="body2" sx={{ mb: 2 }}>
							Format: mm/dd/yyyy
						</Typography>
						<TextField
							label="Date of Birth"
							variant="outlined"
							value={dateOfBirth}
							onKeyUp={checkKeyUp}
							placeholder="mm/dd/yyyy"
							inputMode="numeric"
							inputProps={{ maxLength: 10 }}
							sx={{ marginBottom: 2 }}
							onChange={(e) => setDateOfBirth(formatDobInput(e.target.value))}
						/>
					</>
				)}

				{step === "changePassword" && (
					<>
						<Typography variant="body1" sx={{ mb: 2 }}>
							Please enter a new password
						</Typography>
						<TextField
							label="New Password"
							type="password"
							variant="outlined"
							value={newPassword}
							onKeyUp={checkKeyUp}
							sx={{ marginBottom: 2 }}
							onChange={(e) => setNewPassword(e.target.value)}
						/>
						<TextField
							label="Re-Type Password"
							type="password"
							variant="outlined"
							value={confirmPassword}
							onKeyUp={checkKeyUp}
							sx={{ marginBottom: 2 }}
							onChange={(e) => setConfirmPassword(e.target.value)}
						/>
					</>
				)}

				<Typography variant="body1" color="error" hidden={!errorMessage}>
					{errorMessage}
				</Typography>
				<WrappedButton isBusy={isSaving} caption={buttonCaption} onClick={submit} />
			</Paper>
			<Typography variant="body1" sx={{ fontSize: "12px", fontWeight: 600, position: "absolute", bottom: 0 }}>
				Powered By Ilmir, Inc.
			</Typography>
		</Box>
	);
}
